import { format, isAfter, isValid, parseISO } from "date-fns";
import React from "react";

import { Avatar, ICellRendererParams, Link, PYColumnDef, Text } from "@performyard/py-components";

import AvatarGroup from "../../../../../../components/AvatarGroup";

import { ReviewForm } from "../../../../../../api/services/searchServiceResultTypes/reviewForm";

import toEmployeeUrl from "../../../../../../utils/URLs/Employee";
// utils
import { appendCurrentLocationAsFrom } from "../../../../../../utils/reviewsBreadcrumbs";

const getMultiEmployeeTableColumnCell = (employees: Array<{ _id: string; name: string }>) => {
	if (employees[0]?._id === "_redacted") {
		return (
			<div className="flex h-full items-center gap-1">
				<Avatar size={20} id={"REDACTED"} alt="Redacted user avatar" />
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					Redacted
				</Text>
			</div>
		);
	} else if (employees.length === 1) {
		const employee = employees[0];
		if (employee._id) {
			return (
				<Link href={toEmployeeUrl(employee._id)} isExternal>
					<div className="flex h-full items-center gap-1">
						<Avatar size={20} id={employee._id} name={employee.name} />
						<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
							{employee.name}
						</Text>
					</div>
				</Link>
			);
		} else {
			return (
				<div className="flex h-full items-center gap-1">
					<Avatar size={20} id={employee._id} name={employee.name} />
					<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1 text-neutral-10">
						{employee.name}
					</Text>
				</div>
			);
		}
	} else if (employees.length > 1) {
		return (
			<div className="flex h-full items-center">
				<AvatarGroup
					employees={employees.map(({ _id, name }) => ({ _id, name }))}
					size={20}
					maxLength={5}
					hasLinks
				/>
			</div>
		);
	}
	return null;
};

const ActionCellRenderer = (params: ICellRendererParams<ReviewForm>) => {
	const form = params.data;
	if (!form) return null;

	return (
		<div className="flex h-full items-center">
			<Link
				href={appendCurrentLocationAsFrom(`/review/${form._id}`)}
				underline="never"
				dangerouslySetClassName="inline-flex h-8 items-center justify-center rounded-1 border-none bg-primary-5 px-2 text-paragraph-3 font-semibold text-neutral-0 hover:bg-primary-7 active:bg-primary-8"
			>
				Open Form
			</Link>
		</div>
	);
};

const SubjectCellRenderer = (params: ICellRendererParams<ReviewForm>) => {
	const form = params.data;
	if (!form) return null;

	const subjectID = form?.subject || "";
	const subjectName = form?.subject_name || "";

	return (
		<div className="flex h-full items-center gap-1">
			<Avatar size={20} id={subjectID} name={subjectName} />
			<Link href={toEmployeeUrl(subjectID)} isExternal>
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					{subjectName}
				</Text>
			</Link>
		</div>
	);
};

const AuthorsCellRenderer = (params: ICellRendererParams<ReviewForm>) => {
	const review = params.data;
	if (!review) return null;

	const authors =
		review?.author_objects.length > 0
			? review?.author_objects
			: [
					{
						_id: "",
						name: "Awaiting Assignment",
					},
				];

	return getMultiEmployeeTableColumnCell(authors);
};

const SignersCellRenderer = (params: ICellRendererParams<ReviewForm>) => {
	const review = params.data;
	if (!review) return null;

	const signers =
		review?.signer_objects.length > 0
			? review?.signer_objects
			: [
					{
						_id: "",
						name: "Awaiting Assignment",
					},
				];

	return getMultiEmployeeTableColumnCell(signers);
};

const StatusCellRenderer = (params: ICellRendererParams<ReviewForm>) => {
	const report = params.data;
	if (!report) return null;

	const {
		start_date,
		submit,
		author_objects,
		signer_objects,
		author: currentAuthorID,
		signer: currentSignerID,
	} = report;
	const completed = (report as ReviewForm & { completed?: boolean }).completed;
	const parsedStartDate = parseISO(start_date);
	const isRedacted = author_objects.length > 0 && author_objects[0]?._id === "_redacted";
	const today = new Date();

	let renderedText: string;

	if (completed) {
		renderedText = "Completed";
	} else if (isValid(parsedStartDate) && isAfter(parsedStartDate, today)) {
		renderedText = `Starting ${format(parsedStartDate, "MMM dd, yyyy")}`;
	} else if (submit) {
		const currentSigner = signer_objects.find((signerObject) => signerObject._id === currentSignerID);
		renderedText = `Awaiting sign off by ${currentSigner?.name}`;
	} else {
		const currentAuthor = author_objects.find((authorObject) => authorObject._id === currentAuthorID);
		const renderedString = isRedacted || !currentAuthor?.name ? "" : `by ${currentAuthor?.name}`;
		renderedText = `Awaiting Submission ${renderedString}`;
	}

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
				{renderedText}
			</Text>
		</div>
	);
};

const getAllFormsTableCols = (): PYColumnDef<ReviewForm>[] => {
	return [
		{
			field: "action",
			headerName: "Action",
			width: 116,
			minWidth: 116,
			maxWidth: 116,
			sortable: false,
			resizable: false,
			cellRenderer: ActionCellRenderer,
		},
		{
			field: "form_name",
			headerName: "Form",
			sortable: true,
			resizable: false,
			width: 350,
			minWidth: 350,
			type: "text",
			valueGetter: (p) => p.data?.form_name ?? "",
		},
		{
			field: "subject_name",
			headerName: "Subject",
			sortable: true,
			resizable: false,
			width: 159,
			minWidth: 159,
			cellRenderer: SubjectCellRenderer,
		},
		{
			field: "author_name",
			headerName: "Authors",
			sortable: true,
			resizable: false,
			width: 159,
			minWidth: 159,
			cellRenderer: AuthorsCellRenderer,
		},
		{
			field: "signer_name",
			headerName: "Signers",
			sortable: true,
			resizable: false,
			width: 159,
			minWidth: 159,
			cellRenderer: SignersCellRenderer,
		},
		{
			field: "authors_due_date",
			headerName: "Author's Due Date",
			sortable: true,
			resizable: false,
			width: 148,
			minWidth: 148,
			type: "date",
			cellRendererParams: {
				dateFormat: "MMM dd, yyyy h:mm a",
			},
		},
		{
			field: "signers_due_date",
			headerName: "Signer's Due Date",
			sortable: true,
			resizable: false,
			width: 148,
			minWidth: 148,
			type: "date",
			cellRendererParams: {
				dateFormat: "MMM dd, yyyy h:mm a",
			},
		},
		{
			field: "complete_date",
			headerName: "Completed Date",
			sortable: true,
			resizable: false,
			width: 148,
			minWidth: 148,
			type: "date",
			cellRendererParams: {
				dateFormat: "MMM dd, yyyy h:mm a",
			},
		},
		{
			field: "status",
			headerName: "Status",
			sortable: false,
			resizable: false,
			width: 280,
			minWidth: 280,
			cellRenderer: StatusCellRenderer,
		},
	];
};

export default getAllFormsTableCols;
